import {
  COLOR_BACKGROUND_INVERSE,
  COLOR_GREEN_400,
  COLOR_NEUTRAL_200,
  COLOR_NEUTRAL_500_A,
  COLOR_NEUTRAL_WHITE,
  COLOR_RED_300,
  COLOR_YELLOW_300
} from '@alltrails/denali/tokens';
import type { SvgDefinition } from '../useSvgImages';

/**
 * Pin state types for trail length pins
 */
export type TrailLengthPinState = 'default' | 'active' | 'viewed';
export type TrailLengthPinType = 'trail' | 'saved' | 'completed' | 'closed' | 'priority-high' | 'priority-medium' | 'priority-low';

const getPriorityColor = (type: TrailLengthPinType): string | undefined => {
  switch (type) {
    case 'priority-high':
      return COLOR_RED_300;
    case 'priority-medium':
      return COLOR_YELLOW_300;
    case 'priority-low':
      return COLOR_GREEN_400;
    default:
      return undefined;
  }
};

/**
 * Get background color based on pin state and type
 */
const getBackgroundColor = (state: TrailLengthPinState, type?: TrailLengthPinType): string => {
  if (state === 'active') {
    return COLOR_BACKGROUND_INVERSE;
  }

  const priorityColor = type ? getPriorityColor(type) : undefined;
  if (priorityColor) {
    return priorityColor;
  }

  if (state === 'viewed') {
    return COLOR_NEUTRAL_200;
  }

  return COLOR_NEUTRAL_WHITE;
};

// ============================================================================
// STRETCHABLE PIN SVG (for icon-text-fit approach)
// ============================================================================
const widthWithIcon = 76;
const widthWithoutIcon = 60;
const height = 32;
const stretchX: [number, number][] = [[11, 50]];
const stretchY: [number, number][] = [[8.5, 21.5]];
const content: [number, number, number, number] = [11, 8.5, 50, 21.5];

type CreateStretchablePinSvgArgs = {
  imageName: string;
  state: TrailLengthPinState;
  type: TrailLengthPinType;
};

const createStretchablePinSvg = ({ imageName, state, type }: CreateStretchablePinSvgArgs): SvgDefinition => {
  const backgroundColor = getBackgroundColor(state, type);

  let svgString = '';
  if (type === 'closed') {
    if (state === 'active') {
      svgString = `<svg width="76" height="32" viewBox="0 0 76 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_3282_45662)">
          <path
            d="M47.4443 28C47.4443 30.2091 42.9672 32 37.4443 32C31.9215 32 27.4443 30.2091 27.4443 28C27.4443 25.7909 31.9215 24 37.4443 24C42.9672 24 47.4443 25.7909 47.4443 28Z"
            fill="url(#paint0_radial_3282_45662)" />
          <g filter="url(#filter0_dd_3282_45662)">
            <mask id="path-2-outside-1_3282_45662" maskUnits="userSpaceOnUse" x="2" y="1.5" width="72" height="25"
              fill="black">
              <rect fill="white" x="2" y="1.5" width="72" height="25" />
              <path
                d="M4 14C4 8.20101 8.70101 3.5 14.5 3.5H61.5C67.299 3.5 72 8.20101 72 14C72 19.799 67.299 24.5 61.5 24.5H14.5C8.70101 24.5 4 19.799 4 14Z" />
            </mask>
            <path
              d="M4 14C4 8.20101 8.70101 3.5 14.5 3.5H61.5C67.299 3.5 72 8.20101 72 14C72 19.799 67.299 24.5 61.5 24.5H14.5C8.70101 24.5 4 19.799 4 14Z"
              fill="#1B2818" shape-rendering="crispEdges" />
            <path
              d="M4 14M72 14M72 14M4 14M14.5 3.5V5H61.5V3.5V2H14.5V3.5ZM72 14M61.5 24.5V23H14.5V24.5V26H61.5V24.5ZM4 14M14.5 24.5V23C9.52944 23 5.5 18.9706 5.5 14H4H2.5C2.5 20.6274 7.87259 26 14.5 26V24.5ZM72 14H70.5C70.5 18.9706 66.4706 23 61.5 23V24.5V26C68.1274 26 73.5 20.6274 73.5 14H72ZM61.5 3.5V5C66.4706 5 70.5 9.02944 70.5 14H72H73.5C73.5 7.37258 68.1274 2 61.5 2V3.5ZM14.5 3.5V2C7.87258 2 2.5 7.37258 2.5 14H4H5.5C5.5 9.02944 9.52944 5 14.5 5V3.5Z"
              fill="white" mask="url(#path-2-outside-1_3282_45662)" />
            <circle cx="61" cy="14" r="7" fill="#AAB3A9" />
            <path
              d="M58 14.0001C58 13.8423 58.1119 13.7144 58.25 13.7144H63.75C63.8881 13.7144 64 13.8423 64 14.0001C64 14.1579 63.8881 14.2858 63.75 14.2858H58.25C58.1119 14.2858 58 14.1579 58 14.0001Z"
              fill="#1B2818" stroke="#1B2818" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M42.3105 23.75L38.7373 27.3232C38.0539 28.0066 36.9461 28.0066 36.2627 27.3232L32.6895 23.75H42.3105Z"
              fill="#1B2818" stroke="white" stroke-width="1.5" />
          </g>
          <rect x="28.5557" y="20" width="20" height="4.5" fill="#1B2818" />
        </g>
        <defs>
          <filter id="filter0_dd_3282_45662" x="-1.5" y="0" width="79" height="34.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3282_45662" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_3282_45662" result="effect2_dropShadow_3282_45662" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_3282_45662" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_3282_45662" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(37.4443 28) rotate(90) scale(3.5 8.75)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_3282_45662">
            <rect width="76" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    } else {
      svgString = `<svg width="76" height="32" viewBox="0 0 76 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_3282_45658)">
          <path
            d="M47.4443 28C47.4443 30.2091 42.9672 32 37.4443 32C31.9215 32 27.4443 30.2091 27.4443 28C27.4443 25.7909 31.9215 24 37.4443 24C42.9672 24 47.4443 25.7909 47.4443 28Z"
            fill="url(#paint0_radial_3282_45658)" />
          <g filter="url(#filter0_dd_3282_45658)">
            <rect x="4" y="3.5" width="68" height="21" rx="10.5" fill="${backgroundColor}" shape-rendering="crispEdges" />
            <rect x="3.75" y="3.25" width="68.5" height="21.5" rx="10.75" stroke="#D0D6CE" stroke-width="0.5"
              shape-rendering="crispEdges" />
            <circle cx="61" cy="14" r="7" fill="#8B958A" />
            <path
              d="M58 14.0001C58 13.8423 58.1119 13.7144 58.25 13.7144H63.75C63.8881 13.7144 64 13.8423 64 14.0001C64 14.1579 63.8881 14.2858 63.75 14.2858H58.25C58.1119 14.2858 58 14.1579 58 14.0001Z"
              fill="${backgroundColor}" stroke="${backgroundColor}" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M41.6035 24.25L38.8838 26.9697C38.3957 27.4578 37.6043 27.4578 37.1162 26.9697L34.3965 24.25H41.6035Z"
              fill="${backgroundColor}" stroke="#D0D6CE" stroke-width="0.5" />
          </g>
          <rect x="33" y="20" width="11.1111" height="4.5" fill="${backgroundColor}" />
        </g>
        <defs>
          <filter id="filter0_dd_3282_45658" x="-0.5" y="1" width="77" height="32.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3282_45658" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_3282_45658" result="effect2_dropShadow_3282_45658" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_3282_45658" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_3282_45658" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(37.4443 28) rotate(90) scale(3.5 8.75)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_3282_45658">
            <rect width="76" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    }

    return {
      name: imageName,
      svgString,
      height: 32,
      width: widthWithIcon,
      stretchX,
      stretchY,
      content
    };
  } else if (type === 'completed') {
    if (state === 'active') {
      svgString = `<svg width="76" height="32" viewBox="0 0 76 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_3282_45599)">
          <path
            d="M47.4443 28C47.4443 30.2091 42.9672 32 37.4443 32C31.9215 32 27.4443 30.2091 27.4443 28C27.4443 25.7909 31.9215 24 37.4443 24C42.9672 24 47.4443 25.7909 47.4443 28Z"
            fill="url(#paint0_radial_3282_45599)" />
          <g filter="url(#filter0_dd_3282_45599)">
            <mask id="path-2-outside-1_3282_45599" maskUnits="userSpaceOnUse" x="2" y="1.5" width="72" height="25"
              fill="black">
              <rect fill="white" x="2" y="1.5" width="72" height="25" />
              <path
                d="M4 14C4 8.20101 8.70101 3.5 14.5 3.5H61.5C67.299 3.5 72 8.20101 72 14C72 19.799 67.299 24.5 61.5 24.5H14.5C8.70101 24.5 4 19.799 4 14Z" />
            </mask>
            <path
              d="M4 14C4 8.20101 8.70101 3.5 14.5 3.5H61.5C67.299 3.5 72 8.20101 72 14C72 19.799 67.299 24.5 61.5 24.5H14.5C8.70101 24.5 4 19.799 4 14Z"
              fill="#1B2818" shape-rendering="crispEdges" />
            <path
              d="M4 14M72 14M72 14M4 14M14.5 3.5V5H61.5V3.5V2H14.5V3.5ZM72 14M61.5 24.5V23H14.5V24.5V26H61.5V24.5ZM4 14M14.5 24.5V23C9.52944 23 5.5 18.9706 5.5 14H4H2.5C2.5 20.6274 7.87259 26 14.5 26V24.5ZM72 14H70.5C70.5 18.9706 66.4706 23 61.5 23V24.5V26C68.1274 26 73.5 20.6274 73.5 14H72ZM61.5 3.5V5C66.4706 5 70.5 9.02944 70.5 14H72H73.5C73.5 7.37258 68.1274 2 61.5 2V3.5ZM14.5 3.5V2C7.87258 2 2.5 7.37258 2.5 14H4H5.5C5.5 9.02944 9.52944 5 14.5 5V3.5Z"
              fill="white" mask="url(#path-2-outside-1_3282_45599)" />
            <circle cx="61" cy="14" r="7" fill="#70C55C" />
            <path
              d="M64.0114 11.9913C64.1083 12.0896 64.1072 12.2479 64.0088 12.3448L60.4306 15.8717C60.334 15.9669 60.1791 15.9677 60.0815 15.8735L58.3264 14.18C58.227 14.0841 58.2242 13.9258 58.3201 13.8265C58.416 13.7271 58.5742 13.7243 58.6736 13.8202L60.2532 15.3444L63.6578 11.9887C63.7562 11.8918 63.9145 11.8929 64.0114 11.9913Z"
              fill="#0E110E" stroke="#0E110E" stroke-linecap="round" />
            <path d="M42.3105 23.75L38.7373 27.3232C38.0539 28.0066 36.9461 28.0066 36.2627 27.3232L32.6895 23.75H42.3105Z"
              fill="#1B2818" stroke="white" stroke-width="1.5" />
          </g>
          <rect x="28.5557" y="20" width="20" height="4.5" fill="#1B2818" />
        </g>
        <defs>
          <filter id="filter0_dd_3282_45599" x="-1.5" y="0" width="79" height="34.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3282_45599" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_3282_45599" result="effect2_dropShadow_3282_45599" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_3282_45599" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_3282_45599" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(37.4443 28) rotate(90) scale(3.5 8.75)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_3282_45599">
            <rect width="76" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    } else {
      svgString = `<svg width="76" height="32" viewBox="0 0 76 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_3282_45595)">
          <path
            d="M47.4443 28C47.4443 30.2091 42.9672 32 37.4443 32C31.9215 32 27.4443 30.2091 27.4443 28C27.4443 25.7909 31.9215 24 37.4443 24C42.9672 24 47.4443 25.7909 47.4443 28Z"
            fill="url(#paint0_radial_3282_45595)" />
          <g filter="url(#filter0_dd_3282_45595)">
            <rect x="4" y="3.5" width="68" height="21" rx="10.5" fill="${backgroundColor}" shape-rendering="crispEdges" />
            <rect x="3.75" y="3.25" width="68.5" height="21.5" rx="10.75" stroke="#D0D6CE" stroke-width="0.5"
              shape-rendering="crispEdges" />
            <circle cx="61" cy="14" r="7" fill="#4DA330" />
            <path
              d="M64.0114 11.9913C64.1083 12.0896 64.1072 12.2479 64.0088 12.3448L60.4306 15.8717C60.334 15.9669 60.1791 15.9677 60.0815 15.8735L58.3264 14.18C58.227 14.0841 58.2242 13.9258 58.3201 13.8265C58.416 13.7271 58.5742 13.7243 58.6736 13.8202L60.2532 15.3444L63.6578 11.9887C63.7562 11.8918 63.9145 11.8929 64.0114 11.9913Z"
              fill="${backgroundColor}" stroke="${backgroundColor}" stroke-linecap="round" />
            <path d="M41.6035 24.25L38.8838 26.9697C38.3957 27.4578 37.6043 27.4578 37.1162 26.9697L34.3965 24.25H41.6035Z"
              fill="${backgroundColor}" stroke="#D0D6CE" stroke-width="0.5" />
          </g>
          <rect x="33" y="20" width="11.1111" height="4.5" fill="${backgroundColor}" />
        </g>
        <defs>
          <filter id="filter0_dd_3282_45595" x="-0.5" y="1" width="77" height="32.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3282_45595" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_3282_45595" result="effect2_dropShadow_3282_45595" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_3282_45595" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_3282_45595" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(37.4443 28) rotate(90) scale(3.5 8.75)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_3282_45595">
            <rect width="76" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    }
    return {
      name: imageName,
      svgString,
      height: 32,
      width: widthWithIcon,
      stretchX,
      stretchY,
      content
    };
  } else if (type === 'saved') {
    if (state === 'active') {
      svgString = `<svg width="76" height="32" viewBox="0 0 76 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_3282_45555)">
          <path
            d="M48.1614 28C48.1614 30.2091 43.6121 32 38.0002 32C32.3882 32 27.8389 30.2091 27.8389 28C27.8389 25.7909 32.3882 24 38.0002 24C43.6121 24 48.1614 25.7909 48.1614 28Z"
            fill="url(#paint0_radial_3282_45555)" />
          <g filter="url(#filter0_dd_3282_45555)">
            <mask id="path-2-outside-1_3282_45555" maskUnits="userSpaceOnUse" x="1.5" y="1.5" width="73" height="25"
              fill="black">
              <rect fill="white" x="1.5" y="1.5" width="73" height="25" />
              <path
                d="M3.5 14C3.5 8.20101 8.20101 3.5 14 3.5H62C67.799 3.5 72.5 8.20101 72.5 14C72.5 19.799 67.799 24.5 62 24.5H14C8.20101 24.5 3.5 19.799 3.5 14Z" />
            </mask>
            <path
              d="M3.5 14C3.5 8.20101 8.20101 3.5 14 3.5H62C67.799 3.5 72.5 8.20101 72.5 14C72.5 19.799 67.799 24.5 62 24.5H14C8.20101 24.5 3.5 19.799 3.5 14Z"
              fill="#1B2818" shape-rendering="crispEdges" />
            <path
              d="M3.5 14M72.5 14M72.5 14M3.5 14M14 3.5V5H62V3.5V2H14V3.5ZM72.5 14M62 24.5V23H14V24.5V26H62V24.5ZM3.5 14M14 24.5V23C9.02944 23 5 18.9706 5 14H3.5H2C2 20.6274 7.37259 26 14 26V24.5ZM72.5 14H71C71 18.9706 66.9706 23 62 23V24.5V26C68.6274 26 74 20.6274 74 14H72.5ZM62 3.5V5C66.9706 5 71 9.02944 71 14H72.5H74C74 7.37258 68.6274 2 62 2V3.5ZM14 3.5V2C7.37258 2 2 7.37258 2 14H3.5H5C5 9.02944 9.02944 5 14 5V3.5Z"
              fill="white" mask="url(#path-2-outside-1_3282_45555)" />
            <path
              d="M63.5 8C62.9178 8 62.3437 8.13554 61.823 8.3959C61.3022 8.65625 60.8493 9.03426 60.5 9.5C60.0278 8.87035 59.3694 8.40525 58.6181 8.17057C57.8669 7.9359 57.0608 7.94355 56.3141 8.19244C55.5675 8.44133 54.918 8.91884 54.4578 9.55734C53.9976 10.1958 53.75 10.9629 53.75 11.75C53.75 16.2438 59.9938 19.7875 60.2562 19.9375C60.3306 19.9793 60.4147 20.0009 60.5 20C60.5854 20.0015 60.6696 19.9799 60.7437 19.9375C61.8936 19.2653 62.972 18.4779 63.9625 17.5875C66.1438 15.625 67.25 13.6625 67.25 11.75C67.25 10.7554 66.8549 9.80161 66.1517 9.09835C65.4484 8.39509 64.4946 8 63.5 8Z"
              fill="#E96098" />
            <path d="M42.8105 23.75L39.2373 27.3232C38.5539 28.0066 37.4461 28.0066 36.7627 27.3232L33.1895 23.75H42.8105Z"
              fill="#1B2818" stroke="white" stroke-width="1.5" />
          </g>
          <rect x="28.9678" y="20" width="20.3226" height="4.5" fill="#1B2818" />
        </g>
        <defs>
          <filter id="filter0_dd_3282_45555" x="-2" y="0" width="80" height="34.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3282_45555" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_3282_45555" result="effect2_dropShadow_3282_45555" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_3282_45555" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_3282_45555" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(38.0002 28) rotate(90) scale(3.5 8.89113)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_3282_45555">
            <rect width="76" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    } else {
      svgString = `<svg width="76" height="32" viewBox="0 0 76 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_3282_45553)">
          <path
            d="M48.1614 28C48.1614 30.2091 43.6121 32 38.0002 32C32.3882 32 27.8389 30.2091 27.8389 28C27.8389 25.7909 32.3882 24 38.0002 24C43.6121 24 48.1614 25.7909 48.1614 28Z"
            fill="url(#paint0_radial_3282_45553)" />
          <g filter="url(#filter0_dd_3282_45553)">
            <rect x="3.5" y="3.5" width="69" height="21" rx="10.5" fill="${backgroundColor}" shape-rendering="crispEdges" />
            <rect x="3.25" y="3.25" width="69.5" height="21.5" rx="10.75" stroke="#D0D6CE" stroke-width="0.5"
              shape-rendering="crispEdges" />
            <path
              d="M63.5 8C62.9178 8 62.3437 8.13554 61.823 8.3959C61.3022 8.65625 60.8493 9.03426 60.5 9.5C60.0278 8.87035 59.3694 8.40525 58.6181 8.17057C57.8669 7.9359 57.0608 7.94355 56.3141 8.19244C55.5675 8.44133 54.918 8.91884 54.4578 9.55734C53.9976 10.1958 53.75 10.9629 53.75 11.75C53.75 16.2438 59.9938 19.7875 60.2562 19.9375C60.3306 19.9793 60.4147 20.0009 60.5 20C60.5854 20.0015 60.6696 19.9799 60.7437 19.9375C61.8936 19.2653 62.972 18.4779 63.9625 17.5875C66.1438 15.625 67.25 13.6625 67.25 11.75C67.25 10.7554 66.8549 9.80161 66.1517 9.09835C65.4484 8.39509 64.4946 8 63.5 8Z"
              fill="#C83979" />
            <path d="M42.1035 24.25L39.3838 26.9697C38.8957 27.4578 38.1043 27.4578 37.6162 26.9697L34.8965 24.25H42.1035Z"
              fill="${backgroundColor}" stroke="#D0D6CE" stroke-width="0.5" />
          </g>
          <rect x="33.4839" y="20" width="11.2903" height="4.5" fill="${backgroundColor}" />
        </g>
        <defs>
          <filter id="filter0_dd_3282_45553" x="-1" y="1" width="78" height="32.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3282_45553" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_3282_45553" result="effect2_dropShadow_3282_45553" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_3282_45553" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_3282_45553" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(38.0002 28) rotate(90) scale(3.5 8.89113)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_3282_45553">
            <rect width="76" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    }
    return {
      name: imageName,
      svgString,
      height: 32,
      width: widthWithIcon,
      stretchX,
      stretchY,
      content
    };
  } else if (type === 'priority-high' || type === 'priority-medium' || type === 'priority-low') {
    const priorityColor = getPriorityColor(type) as string;
    if (state === 'active') {
      svgString = `<svg width="60" height="32" viewBox="0 0 60 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_priority_active)">
          <path
            d="M39.7198 28C39.7198 30.2091 35.368 32 29.9998 32C24.6316 32 20.2798 30.2091 20.2798 28C20.2798 25.7909 24.6316 24 29.9998 24C35.368 24 39.7198 25.7909 39.7198 28Z"
            fill="url(#paint0_radial_priority_active)" />
          <g filter="url(#filter0_dd_priority_active)">
            <mask id="path-2-outside-1_priority_active" maskUnits="userSpaceOnUse" x="1.5" y="1.5" width="57" height="25"
              fill="black">
              <rect fill="white" x="1.5" y="1.5" width="57" height="25" />
              <path
                d="M3.5 14C3.5 8.20101 8.20101 3.5 14 3.5H46C51.799 3.5 56.5 8.20101 56.5 14C56.5 19.799 51.799 24.5 46 24.5H14C8.20101 24.5 3.5 19.799 3.5 14Z" />
            </mask>
            <path
              d="M3.5 14C3.5 8.20101 8.20101 3.5 14 3.5H46C51.799 3.5 56.5 8.20101 56.5 14C56.5 19.799 51.799 24.5 46 24.5H14C8.20101 24.5 3.5 19.799 3.5 14Z"
              fill="#1B2818" shape-rendering="crispEdges" />
            <path
              d="M3.5 14M56.5 14M56.5 14M3.5 14M14 3.5V5H46V3.5V2H14V3.5ZM56.5 14M46 24.5V23H14V24.5V26H46V24.5ZM3.5 14M14 24.5V23C9.02944 23 5 18.9706 5 14H3.5H2C2 20.6274 7.37258 26 14 26V24.5ZM56.5 14H55C55 18.9706 50.9706 23 46 23V24.5V26C52.6274 26 58 20.6274 58 14H56.5ZM46 3.5V5C50.9706 5 55 9.02944 55 14H56.5H58C58 7.37258 52.6274 2 46 2V3.5ZM14 3.5V2C7.37258 2 2 7.37258 2 14H3.5H5C5 9.02944 9.02944 5 14 5V3.5Z"
              fill="${priorityColor}" mask="url(#path-2-outside-1_priority_active)" />
            <path d="M35.3105 23.75L31.7373 27.3232C31.0539 28.0066 29.9461 28.0066 29.2627 27.3232L25.6895 23.75H35.3105Z"
              fill="#1B2818" stroke="${priorityColor}" stroke-width="1.5" />
          </g>
          <rect x="21.3599" y="20" width="19.44" height="4.5" fill="#1B2818" />
        </g>
        <defs>
          <filter id="filter0_dd_priority_active" x="-2" y="0" width="64" height="34.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_priority_active" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_priority_active" result="effect2_dropShadow_priority_active" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_priority_active" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_priority_active" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(29.9998 28) rotate(90) scale(3.5 8.505)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_priority_active">
            <rect width="60" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    } else {
      svgString = `<svg width="60" height="32" viewBox="0 0 60 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_priority_default)">
          <path
            d="M39.7198 28C39.7198 30.2091 35.368 32 29.9998 32C24.6316 32 20.2798 30.2091 20.2798 28C20.2798 25.7909 24.6316 24 29.9998 24C35.368 24 39.7198 25.7909 39.7198 28Z"
            fill="url(#paint0_radial_priority_default)" />
          <g filter="url(#filter0_dd_priority_default)">
            <rect x="3.5" y="3.5" width="53" height="21" rx="10.5" fill="${backgroundColor}" shape-rendering="crispEdges" />
            <rect x="3.25" y="3.25" width="53.5" height="21.5" rx="10.75" stroke="${COLOR_NEUTRAL_500_A}" stroke-width="0.5"
              shape-rendering="crispEdges" />
            <path d="M34.1035 24.25L31.3838 26.9697C30.8957 27.4578 30.1043 27.4578 29.6162 26.9697L26.8965 24.25H34.1035Z"
              fill="${backgroundColor}" stroke="${COLOR_NEUTRAL_500_A}" stroke-width="0.5" />
          </g>
          <rect x="24.6001" y="20" width="10.8" height="4.5" fill="${backgroundColor}" />
        </g>
        <defs>
          <filter id="filter0_dd_priority_default" x="-1" y="1" width="62" height="32.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_priority_default" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_priority_default" result="effect2_dropShadow_priority_default" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_priority_default" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_priority_default" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(29.9998 28) rotate(90) scale(3.5 8.505)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_priority_default">
            <rect width="60" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    }
    return {
      name: imageName,
      svgString,
      height,
      width: widthWithoutIcon,
      stretchX,
      stretchY,
      content
    };
  } else {
    if (state === 'active') {
      svgString = `<svg width="60" height="32" viewBox="0 0 60 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_3282_45741)">
          <path
            d="M39.7198 28C39.7198 30.2091 35.368 32 29.9998 32C24.6316 32 20.2798 30.2091 20.2798 28C20.2798 25.7909 24.6316 24 29.9998 24C35.368 24 39.7198 25.7909 39.7198 28Z"
            fill="url(#paint0_radial_3282_45741)" />
          <g filter="url(#filter0_dd_3282_45741)">
            <mask id="path-2-outside-1_3282_45741" maskUnits="userSpaceOnUse" x="1.5" y="1.5" width="57" height="25"
              fill="black">
              <rect fill="white" x="1.5" y="1.5" width="57" height="25" />
              <path
                d="M3.5 14C3.5 8.20101 8.20101 3.5 14 3.5H46C51.799 3.5 56.5 8.20101 56.5 14C56.5 19.799 51.799 24.5 46 24.5H14C8.20101 24.5 3.5 19.799 3.5 14Z" />
            </mask>
            <path
              d="M3.5 14C3.5 8.20101 8.20101 3.5 14 3.5H46C51.799 3.5 56.5 8.20101 56.5 14C56.5 19.799 51.799 24.5 46 24.5H14C8.20101 24.5 3.5 19.799 3.5 14Z"
              fill="#1B2818" shape-rendering="crispEdges" />
            <path
              d="M3.5 14M56.5 14M56.5 14M3.5 14M14 3.5V5H46V3.5V2H14V3.5ZM56.5 14M46 24.5V23H14V24.5V26H46V24.5ZM3.5 14M14 24.5V23C9.02944 23 5 18.9706 5 14H3.5H2C2 20.6274 7.37258 26 14 26V24.5ZM56.5 14H55C55 18.9706 50.9706 23 46 23V24.5V26C52.6274 26 58 20.6274 58 14H56.5ZM46 3.5V5C50.9706 5 55 9.02944 55 14H56.5H58C58 7.37258 52.6274 2 46 2V3.5ZM14 3.5V2C7.37258 2 2 7.37258 2 14H3.5H5C5 9.02944 9.02944 5 14 5V3.5Z"
              fill="white" mask="url(#path-2-outside-1_3282_45741)" />
            <path d="M35.3105 23.75L31.7373 27.3232C31.0539 28.0066 29.9461 28.0066 29.2627 27.3232L25.6895 23.75H35.3105Z"
              fill="#1B2818" stroke="white" stroke-width="1.5" />
          </g>
          <rect x="21.3599" y="20" width="19.44" height="4.5" fill="#1B2818" />
        </g>
        <defs>
          <filter id="filter0_dd_3282_45741" x="-2" y="0" width="64" height="34.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3282_45741" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_3282_45741" result="effect2_dropShadow_3282_45741" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_3282_45741" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_3282_45741" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(29.9998 28) rotate(90) scale(3.5 8.505)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_3282_45741">
            <rect width="60" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    } else {
      svgString = `<svg width="60" height="32" viewBox="0 0 60 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_3282_45724)">
          <path
            d="M39.7198 28C39.7198 30.2091 35.368 32 29.9998 32C24.6316 32 20.2798 30.2091 20.2798 28C20.2798 25.7909 24.6316 24 29.9998 24C35.368 24 39.7198 25.7909 39.7198 28Z"
            fill="url(#paint0_radial_3282_45724)" />
          <g filter="url(#filter0_dd_3282_45724)">
            <rect x="3.5" y="3.5" width="53" height="21" rx="10.5" fill="${backgroundColor}" shape-rendering="crispEdges" />
            <rect x="3.25" y="3.25" width="53.5" height="21.5" rx="10.75" stroke="#D0D6CE" stroke-width="0.5"
              shape-rendering="crispEdges" />
            <path d="M34.1035 24.25L31.3838 26.9697C30.8957 27.4578 30.1043 27.4578 29.6162 26.9697L26.8965 24.25H34.1035Z"
              fill="${backgroundColor}" stroke="#D0D6CE" stroke-width="0.5" />
          </g>
          <rect x="24.6001" y="20" width="10.8" height="4.5" fill="${backgroundColor}" />
        </g>
        <defs>
          <filter id="filter0_dd_3282_45724" x="-1" y="1" width="62" height="32.5858" filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3282_45724" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_3282_45724" result="effect2_dropShadow_3282_45724" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_3282_45724" result="shape" />
          </filter>
          <radialGradient id="paint0_radial_3282_45724" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
            gradientTransform="translate(29.9998 28) rotate(90) scale(3.5 8.505)">
            <stop stop-opacity="0.24" />
            <stop offset="1" stop-color="#8F8F8F" stop-opacity="0" />
          </radialGradient>
          <clipPath id="clip0_3282_45724">
            <rect width="60" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>`;
    }
    return {
      name: imageName,
      svgString,
      height,
      width: widthWithoutIcon,
      stretchX,
      stretchY,
      content
    };
  }
};

/**
 * Generates a consistent image name for a stretchable trail length pin variant.
 */
export const getStretchablePinImageName = (state: TrailLengthPinState, type: TrailLengthPinType): string => {
  return `trail-length-pin-stretchable-${state}-${type}`;
};

/**
 * Generates all stretchable trail length pin SVG variants.
 * Only state × type combinations are needed (12 total), since the pin stretches to fit any text.
 */
export const generateStretchablePinVariants = (): SvgDefinition[] => {
  const states: TrailLengthPinState[] = ['default', 'active', 'viewed'];
  const types: TrailLengthPinType[] = ['trail', 'saved', 'completed', 'closed', 'priority-high', 'priority-medium', 'priority-low'];
  const definitions: SvgDefinition[] = [];

  for (const state of states) {
    for (const type of types) {
      const imageName = getStretchablePinImageName(state, type);
      definitions.push(createStretchablePinSvg({ imageName, state, type }));
    }
  }

  return definitions;
};
